import React, { useState } from 'react';
import Select, { ValueType } from 'react-select';
import {
  getStatusStyles,
  QueueType,
  AccessProvisioningStatus,
  GesActionState,
  TodayActionsProps,
  ReservationCardProps,
  formatTimestamp,
  getQueuePillClass,
  computeButtonVisibility
} from './helper';
import { EyeIcon, EyeOffIcon } from '../ui/Icons';

const StatusPill = ({ status }: { status: string }) => {
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-base font-gilroy-medium uppercase tracking-wide md:px-3 md:py-1  ${getStatusStyles(
        status
      )}`}
    >
      {status}
    </span>
  );
};

const getReservationStatusLabel = ({
  actualCheckInAt,
  actualCheckOutAt
}: {
  actualCheckInAt?: number | null;
  actualCheckOutAt?: number | null;
}) => {
  if (actualCheckOutAt) {
    return 'check out';
  }

  if (actualCheckInAt) {
    return 'check in';
  }

  return 'not check in';
};

const QueuePill = ({ type }: { type: QueueType }) => {
  const className = getQueuePillClass(type);

  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-base font-gilroy-medium uppercase tracking-wide md:px-3 md:py-1  ${className}`}
    >
      {type}
    </span>
  );
};

const ACCESS_OPTIONS: { value: AccessProvisioningStatus; label: string }[] = [
  { value: 'In-Progress', label: 'In-Progress' },
  { value: 'Done', label: 'Done' },
  { value: 'Pending Option 1', label: 'Pending Option 1' },
  { value: 'Pending Option 2', label: 'Pending Option 2' }
];

const UPCOMING_ACCESS_OPTIONS = ACCESS_OPTIONS.filter(
  (option) => option.value === 'In-Progress' || option.value === 'Done'
);

const UpcomingActions = ({
  item,
  actionState,
  onAccessChange
}: {
  item: { id: string };
  actionState: GesActionState;
  onAccessChange: (reservationId: string, status: AccessProvisioningStatus) => void;
}) => {
  return (
    <div className="mt-3 rounded-xl border border-primary-100 bg-primary-50/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-lg md:text-2xl font-gilroy-bold uppercase tracking-wide text-neutral-700">
          Actions
        </h4>
        <div className="flex items-center gap-3">
          <label className="text-lg mb-0 font-semibold font-gilroy-medium text-neutral-600">
            Access Provisioning
          </label>
          <div className="min-w-[180px] text-sm md:text-base">
            <Select
              value={
                UPCOMING_ACCESS_OPTIONS.find(
                  (option) =>
                    option.value ===
                    (actionState.accessProvisioningStatus === 'Done' ? 'Done' : 'In-Progress')
                ) || UPCOMING_ACCESS_OPTIONS[0]
              }
              options={UPCOMING_ACCESS_OPTIONS}
              classNamePrefix="react-select"
              onChange={(
                value: ValueType<{ value: AccessProvisioningStatus; label: string }, false>
              ) => onAccessChange(item.id, value ? value.value : 'In-Progress')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const TodayActions = ({
  item,
  actionState,
  onAccessChange,
  onCheckIn,
  onCheckOut,
  onInspectionDone,
  onInspectionRejected
}: TodayActionsProps) => {
  const [showActionDetails, setShowActionDetails] = useState(false);

  const isUnitInspected = !!item.unitInspectedAt && !!item.isUnitInspected;

  const { showCheckIn, showCheckInWithoutInspection, showCheckOut, showInspectionActions } =
    computeButtonVisibility(item, isUnitInspected, actionState.gesCheckInAt, item.actualCheckInAt);

  const baseButtonClass =
    'rounded-lg border px-4 py-3 text-xl font-gilroy-medium font-semibold  transition-colors disabled:cursor-not-allowed disabled:opacity-50';

  const checkInButton =
    showCheckIn && !item.actualCheckInAt ? (
      <button
        type="button"
        className={`${baseButtonClass} border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
        onClick={() => onCheckIn(item.id, true)}
      >
        Check In
      </button>
    ) : item.actualCheckInAt ? (
      <span className="rounded-lg border border-primary-200 bg-primary-50/20 px-4 py-3 text-lg font-gilroy-medium text-primary-700 shadow-sm">
        <span className="block">Checked In: </span>
        {formatTimestamp(item.actualCheckInAt)}
      </span>
    ) : null;

  const checkInWithoutInspectionButton =
    showCheckInWithoutInspection && !item.actualCheckInAt ? (
      <button
        type="button"
        className={`${baseButtonClass} border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100`}
        onClick={() => onCheckIn(item.id, false)}
      >
        Check In Without Inspection
      </button>
    ) : item.actualCheckInAt ? (
      <span className="items-center rounded-lg border border-primary-200 bg-primary-50/20 px-4 py-3 text-lg font-gilroy-medium text-primary-700 shadow-sm">
        <span className="block"> Checked In Without Inspection: </span>
        {formatTimestamp(item.actualCheckInAt)}
      </span>
    ) : null;

  const checkOutButton =
    showCheckOut && !item.actualCheckOutAt ? (
      <button
        type="button"
        className={`${baseButtonClass} border-primary-300 bg-primary-50/20 text-primary-700 hover:bg-primary-100`}
        onClick={() => onCheckOut(item.id)}
      >
        Check Out
      </button>
    ) : item.actualCheckOutAt ? (
      <span className="items-center rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-lg font-gilroy-medium text-primary-700 shadow-sm">
        <span className="block"> Checked Out:</span> {formatTimestamp(item.actualCheckOutAt)}
      </span>
    ) : null;

  const inspectionDoneButton = showInspectionActions ? (
    <button
      type="button"
      className={`${baseButtonClass} border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100`}
      onClick={() => onInspectionDone(item.id)}
    >
      Inspection Done
    </button>
  ) : null;

  const inspectionRejectedButton = showInspectionActions ? (
    <button
      type="button"
      className={`${baseButtonClass} border-red-300 bg-red-50 text-red-700 hover:bg-red-100`}
      onClick={() => onInspectionRejected(item.id)}
    >
      Inspection Rejected
    </button>
  ) : null;

  const actionButtons = [
    checkInButton,
    checkInWithoutInspectionButton,
    checkOutButton,
    inspectionDoneButton,
    inspectionRejectedButton
  ].filter(Boolean);

  return (
    <div className="mt-3 rounded-xl border border-primary-100 bg-primary-50/40 p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h4 className="text-lg md:text-2xl font-gilroy-bold uppercase tracking-wide text-neutral-700">
            Actions
          </h4>
          <button
            type="button"
            aria-label={showActionDetails ? 'Hide details' : 'Show details'}
            title={showActionDetails ? 'Hide details' : 'Show details'}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-neutral-600 hover:bg-gray-50"
            onClick={() => setShowActionDetails((prev) => !prev)}
          >
            {showActionDetails ? <EyeOffIcon size="16px" /> : <EyeIcon size="16px" />}
          </button>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-lg mb-0  font-semibold font-gilroy-medium text-neutral-600">
            Access Provisioning
          </label>
          <div className="min-w-[180px] text-sm md:text-base">
            <Select
              value={
                ACCESS_OPTIONS.find(
                  (option) => option.value === actionState.accessProvisioningStatus
                ) || ACCESS_OPTIONS[0]
              }
              options={ACCESS_OPTIONS}
              classNamePrefix="react-select"
              onChange={(
                value: ValueType<{ value: AccessProvisioningStatus; label: string }, false>
              ) => onAccessChange(item.id, value ? value.value : 'In-Progress')}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">{actionButtons}</div>

      {showActionDetails && (
        <div className="mt-3 grid grid-cols-2 gap-3 text-base md:text-lg text-neutral-600">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm md:text-base font-gilroy-semibold text-neutral-500">Inspected</p>
            <p className="text-lg md:text-2xl font-gilroy-semibold text-neutral-700">
              {actionState.isUnitInspected || item.isUnitInspected ? 'Yes' : 'No'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm md:text-base font-gilroy-semibold text-neutral-500">
              Inspection Date
            </p>
            <p className="text-lg md:text-2xl font-gilroy-semibold text-neutral-700">
              {item.unitInspectedAt
                ? formatTimestamp(item.unitInspectedAt)
                : formatTimestamp(actionState.inspectionDate)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm md:text-base font-gilroy-semibold text-neutral-500">
              GES Check In
            </p>
            <p className="text-lg md:text-2xl font-gilroy-semibold text-neutral-700">
              {actionState.gesCheckInAt
                ? formatTimestamp(actionState.gesCheckInAt)
                : item.actualCheckInAt
                ? formatTimestamp(item.actualCheckInAt)
                : 'N/A'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm md:text-base font-gilroy-semibold text-neutral-500">
              GES Check Out
            </p>
            <p className="text-lg md:text-2xl font-gilroy-semibold text-neutral-700">
              {actionState.gesCheckOutAt
                ? formatTimestamp(actionState.gesCheckOutAt)
                : item.actualCheckOutAt
                ? formatTimestamp(item.actualCheckOutAt)
                : 'N/A'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const ReservationCard = ({
  item,
  showQueue,
  showTodayActions,
  showUpcomingActions,
  actionState,
  onAccessChange,
  onCheckIn,
  onCheckOut,
  onInspectionDone,
  onInspectionRejected
}: ReservationCardProps) => {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm md:p-6">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2 md:mb-5 md:gap-3">
        <h3 className="text-2xl font-gilroy-medium font-semibold text-neutral-700 md:text-3xl">
          {item.guestName} <span>#id:{item.reservationId}</span>
        </h3>

        <div className="flex items-center gap-2">
          {showQueue && <QueuePill type={item.queueType} />}
          <StatusPill
            status={getReservationStatusLabel({
              actualCheckInAt: actionState.gesCheckInAt || item.actualCheckInAt,
              actualCheckOutAt: actionState.gesCheckOutAt || item.actualCheckOutAt
            })}
          />
        </div>
      </div>

      <div className="mb-2 text-base font-gilroy-medium text-neutral-700 md:mb-3 md:text-lg">
        <span className="text-neutral-500">Dates:</span> {item.checkIn} to {item.checkOut}
      </div>

      <div className="grid grid-cols-2 gap-2 text-lg md:gap-3 md:text-xl">
        <div className="rounded-lg bg-gray-50 p-2.5 md:p-3.5">
          <p className="text-neutral-500">Project</p>
          <p className="text-xl font-gilroy-medium text-neutral-700 md:text-2xl">{item.project}</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-2.5 md:p-3.5">
          <p className="text-neutral-500">Arrivals / Departures</p>
          <p className="text-xl font-gilroy-medium text-neutral-700 md:text-2xl">
            {item.queueType}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 p-2.5 md:p-3.5">
          <p className="text-neutral-500">Area/ Room </p>
          <p className="text-xl font-gilroy-medium text-neutral-700 md:text-2xl">
            {item.area} / {item.roomName}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 p-2.5 md:p-3.5">
          <p className="text-neutral-500">Phone</p>
          <p className="text-xl font-gilroy-medium text-neutral-700 md:text-2xl">{item.phone}</p>
        </div>
      </div>

      {showTodayActions && (
        <TodayActions
          item={item}
          actionState={actionState}
          onAccessChange={onAccessChange}
          onCheckIn={onCheckIn}
          onCheckOut={onCheckOut}
          onInspectionDone={onInspectionDone}
          onInspectionRejected={onInspectionRejected}
        />
      )}

      {showUpcomingActions && (
        <UpcomingActions item={item} actionState={actionState} onAccessChange={onAccessChange} />
      )}
    </div>
  );
};
